import { Loader2, Upload, X } from "lucide-react";
import React, { useCallback, useRef, useState } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";

interface UploadProgress {
  fileName: string;
  progress: number;
  status: "uploading" | "success" | "error";
  error?: string;
}

interface UploadDropzoneProps {
  onUpload: (files: File[]) => Promise<void>;
  disabled?: boolean;
}

const UploadDropzone: React.FC<UploadDropzoneProps> = ({
  onUpload,
  disabled = false,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (disabled || isUploading) return;

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        await handleFiles(files);
      }
    },
    [disabled, isUploading]
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled || isUploading) return;

      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        await handleFiles(files);
      }

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [disabled, isUploading]
  );

  const handleFiles = async (files: File[]) => {
    setIsUploading(true);

    // Initialize upload progress for each file
    const initialProgress: UploadProgress[] = files.map((file) => ({
      fileName: file.name,
      progress: 0,
      status: "uploading",
    }));
    setUploads(initialProgress);

    try {
      await onUpload(files);

      // Mark all as success
      setUploads((prev) =>
        prev.map((u) => ({ ...u, progress: 100, status: "success" }))
      );

      // Clear after delay
      setTimeout(() => {
        setUploads([]);
      }, 2000);
    } catch (error) {
      // Mark as error
      setUploads((prev) =>
        prev.map((u) => ({
          ...u,
          status: "error",
          error: error instanceof Error ? error.message : "Upload failed",
        }))
      );
    } finally {
      setIsUploading(false);
    }
  };

  const clearUploads = () => {
    setUploads([]);
  };

  return (
    <div className="space-y-3">
      {/* Dropzone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && !isUploading && fileInputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
          ${
            isDragOver
              ? isDarkMode
                ? "border-teal-400 bg-teal-900/20"
                : "border-teal-500 bg-teal-50"
              : isDarkMode
                ? "border-rp-separator hover:border-rp-grey/50 bg-rp-surface/50"
                : "border-gray-300 hover:border-gray-400 bg-gray-50"
          }
          ${disabled || isUploading ? "opacity-50 cursor-not-allowed" : ""}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled || isUploading}
        />

        <div className="flex flex-col items-center space-y-2">
          {isUploading ? (
            <Loader2
              className={`w-8 h-8 animate-spin ${isDarkMode ? "text-teal-400" : "text-teal-500"}`}
            />
          ) : (
            <Upload
              className={`w-8 h-8 ${
                isDragOver
                  ? isDarkMode
                    ? "text-teal-400"
                    : "text-teal-500"
                  : isDarkMode
                    ? "text-rp-grey/50"
                    : "text-gray-500"
              }`}
            />
          )}
          <div>
            <p
              className={`text-sm font-medium ${isDarkMode ? "text-rp-grey" : "text-gray-700"}`}
            >
              {isUploading
                ? "Uploading..."
                : "Drag and drop files here, or click to browse"}
            </p>
            <p
              className={`text-xs mt-1 ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}
            >
              Supports images, PDFs, and other documents
            </p>
          </div>
        </div>
      </div>

      {/* Upload progress */}
      {uploads.length > 0 && (
        <div
          className={`rounded-lg border ${isDarkMode ? "bg-rp-surface border-rp-separator" : "bg-white border-gray-200"} p-3`}
        >
          <div className="flex items-center justify-between mb-2">
            <span
              className={`text-sm font-medium ${isDarkMode ? "text-rp-grey" : "text-gray-700"}`}
            >
              {isUploading ? "Uploading files..." : "Upload complete"}
            </span>
            {!isUploading && (
              <button
                onClick={clearUploads}
                className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-rp-surface-raised`}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="space-y-2">
            {uploads.map((upload, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm truncate ${isDarkMode ? "text-rp-grey/70" : "text-gray-600"}`}
                  >
                    {upload.fileName}
                  </p>
                  {upload.status === "uploading" && (
                    <div
                      className={`mt-1 h-1.5 rounded-full ${isDarkMode ? "bg-rp-surface" : "bg-gray-200"}`}
                    >
                      <div
                        className="h-full rounded-full bg-teal-500 transition-all"
                        style={{ width: `${upload.progress}%` }}
                      />
                    </div>
                  )}
                  {upload.status === "error" && (
                    <p className="text-xs text-red-500 mt-1">{upload.error}</p>
                  )}
                </div>
                <div>
                  {upload.status === "success" && (
                    <span className="text-green-500 text-sm">Done</span>
                  )}
                  {upload.status === "error" && (
                    <span className="text-red-500 text-sm">Failed</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadDropzone;

